/**
 * EU Taxonomy Framework Page
 * Taxonomy Alignment Navigator with activities, KPIs, and Article 8 reporting
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Download,
  Leaf,
  Wind,
  Droplets,
  Recycle,
  Trees,
  Shield,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  FileText,
  Eye,
  TrendingUp,
  Loader2,
  Circle,
  BarChart3,
  Target,
  Sparkles,
  Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { apiFetch } from '../services/api_service';


// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function EUTaxonomyPage() {
  const navigate = useNavigate();
  const [filterObjective, setFilterObjective] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [assessmentData, setAssessmentData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [kpisData, setKpisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch assessment overview
        const assessmentRes = await apiFetch(`/v1/taxonomy/assessment`, { headers });
        if (assessmentRes && !assessmentRes._error) {
          setAssessmentData(assessmentRes);
        }

        // Fetch activities
        const params = new URLSearchParams();
        if (filterObjective !== 'all') params.append('objective', filterObjective);
        if (filterStatus !== 'all') params.append('status', filterStatus);
        
        const activitiesRes = await apiFetch(`/v1/taxonomy/activities?${params}`, { headers });
        if (activitiesRes && !activitiesRes._error) {
          setActivities(activitiesRes.activities || []);
        }

        // Fetch objectives
        const objectivesRes = await apiFetch(`/v1/taxonomy/objectives`, { headers });
        if (objectivesRes && !objectivesRes._error) {
          setObjectives(objectivesRes.objectives || []);
        }

        // Fetch KPIs
        const kpisRes = await apiFetch(`/v1/taxonomy/kpis`, { headers });
        if (kpisRes && !kpisRes._error) {
          setKpisData(kpisRes);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching taxonomy data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [filterObjective, filterStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-white">Error loading taxonomy data: {error}</p>
        </div>
      </div>
    );
  }

  const kpis = assessmentData?.kpis || { turnover: 0, capex: 0, opex: 0 };
  const alignmentPercentage = assessmentData?.overall_alignment || 0;
  const flywheelProgress = assessmentData?.flywheel_progress || {};

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="eu-taxonomy-page">
      {/* Breadcrumb */}
      <div className="border-b border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link 
            to="/frameworks" 
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-emerald-400 transition-colors"
            data-testid="back-to-frameworks"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Frameworks
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <TaxonomyHero 
        alignmentPercentage={alignmentPercentage}
        activitiesAssessed={activities.length}
        totalActivities={assessmentData?.total_activities || 0}
        kpis={kpis}
      />

      {/* Flywheel Progress */}
      <div className="border-b border-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <TaxonomyFlywheelTracker progress={flywheelProgress} />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="activities" className="space-y-6">
          <TabsList className="bg-[#1A1A1A] border border-[#2A2A2A]">
            <TabsTrigger value="activities" data-testid="tab-activities">
              Activities
              <Badge variant="secondary" className="ml-2 bg-emerald-500/20 text-emerald-400">
                {activities.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="objectives" data-testid="tab-objectives">Objectives</TabsTrigger>
            <TabsTrigger value="kpis" data-testid="tab-kpis">KPIs</TabsTrigger>
            <TabsTrigger value="evidence" data-testid="tab-evidence">Evidence</TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report">Article 8 Report</TabsTrigger>
            <TabsTrigger value="ai-assistant" data-testid="tab-ai-assistant" className="text-purple-400">
              <Sparkles className="h-3.5 w-3.5 mr-1" />
              AI Assistant
            </TabsTrigger>
          </TabsList>

          {/* ACTIVITIES TAB */}
          <TabsContent value="activities" className="space-y-6">
            {/* Filters & Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <select
                  value={filterObjective}
                  onChange={(e) => setFilterObjective(e.target.value)}
                  className="px-3 py-2 text-sm bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white"
                  data-testid="filter-objective"
                >
                  <option value="all">All Objectives</option>
                  <option value="climate_mitigation">Climate Mitigation</option>
                  <option value="climate_adaptation">Climate Adaptation</option>
                  <option value="water">Water & Marine</option>
                  <option value="circular_economy">Circular Economy</option>
                  <option value="pollution">Pollution Prevention</option>
                  <option value="biodiversity">Biodiversity</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 text-sm bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-white"
                  data-testid="filter-status"
                >
                  <option value="all">All Status</option>
                  <option value="aligned">✅ Aligned</option>
                  <option value="in_progress">⏳ In Progress</option>
                  <option value="not_aligned">❌ Not Aligned</option>
                  <option value="not_started">○ Not Started</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" className="border-[#2A2A2A] text-gray-300 hover:bg-[#1A1A1A]">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Activity
                </Button>
                <Button variant="outline" className="border-[#2A2A2A] text-gray-300 hover:bg-[#1A1A1A]">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>

            {/* Activity Cards Grid */}
            <div className="grid gap-4">
              {activities.map(activity => (
                <ActivityCard 
                  key={activity.id} 
                  activity={activity}
                  onViewDetails={() => navigate(`/frameworks/eu-taxonomy/activities/${activity.id}`)}
                />
              ))}

              {activities.length === 0 && (
                <Card className="bg-[#141414] border-[#2A2A2A]">
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <Leaf className="h-12 w-12 text-gray-500 mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">
                      No activities assessed yet
                    </h3>
                    <p className="text-sm text-gray-400 max-w-md mb-4">
                      Start your EU Taxonomy assessment by identifying which economic 
                      activities your company performs.
                    </p>
                    <Button className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First Activity
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* OBJECTIVES TAB */}
          <TabsContent value="objectives">
            <ObjectivesOverview objectives={objectives} />
          </TabsContent>

          {/* KPIs TAB */}
          <TabsContent value="kpis">
            <KPIsDashboard kpis={kpisData} activities={activities} />
          </TabsContent>

          {/* EVIDENCE TAB */}
          <TabsContent value="evidence">
            <TaxonomyEvidenceHub activities={activities} />
          </TabsContent>

          {/* ARTICLE 8 REPORT TAB */}
          <TabsContent value="report">
            <Article8Report kpis={kpisData} activities={activities} />
          </TabsContent>

          {/* AI ASSISTANT TAB */}
          <TabsContent value="ai-assistant">
            <AIAssessmentAssistant />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ============================================================================
// HERO COMPONENT
// ============================================================================

function TaxonomyHero({ alignmentPercentage, activitiesAssessed, totalActivities, kpis }) {
  const getStatusConfig = (percentage) => {
    if (percentage >= 80) return { icon: CheckCircle2, color: 'text-emerald-400', label: 'Strong alignment' };
    if (percentage >= 50) return { icon: TrendingUp, color: 'text-yellow-400', label: 'Moderate alignment' };
    return { icon: AlertTriangle, color: 'text-orange-400', label: 'Limited alignment' };
  };

  const status = getStatusConfig(alignmentPercentage);
  const StatusIcon = status.icon;

  return (
    <div className="bg-gradient-to-br from-emerald-900/50 via-emerald-800/30 to-teal-900/50">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <h1 className="text-3xl font-bold text-white">EU Taxonomy for Sustainable Activities</h1>
            <Badge className="bg-red-500/20 text-red-300 border-red-500/30">
              Mandatory
            </Badge>
          </div>
          <p className="text-emerald-200/80 text-lg">
            Classification system · 6 Environmental Objectives
          </p>
        </div>

        {/* Alignment Summary Card */}
        <Card className="bg-white/10 backdrop-blur-sm border-white/20">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left: Alignment Progress */}
              <div>
                <h3 className="text-sm font-semibold text-white/90 mb-3">
                  Your Taxonomy-Aligned Activities
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white/80">Overall Alignment</span>
                      <span className="text-3xl font-bold text-white">
                        {alignmentPercentage}%
                      </span>
                    </div>
                    <Progress 
                      value={alignmentPercentage} 
                      className="h-3 bg-white/20"
                    />
                    <p className="text-xs text-white/70 mt-2">
                      {activitiesAssessed} of {totalActivities} activities assessed
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <StatusIcon className={`h-5 w-5 ${status.color}`} />
                    <span className="text-sm text-white/90">{status.label}</span>
                  </div>
                </div>
              </div>

              {/* Right: KPIs (Article 8) */}
              <div>
                <h3 className="text-sm font-semibold text-white/90 mb-3">
                  KPIs (Article 8 Disclosure)
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <KPICard label="Turnover" value={kpis.turnover} />
                  <KPICard label="CapEx" value={kpis.capex} />
                  <KPICard label="OpEx" value={kpis.opex} />
                </div>
                <div className="mt-3 p-2 rounded bg-white/10 border border-white/20 text-center">
                  <span className="text-xs text-white/80">
                    {kpis.status === 'complete' ? '✓ Ready for disclosure' : '⚠️ Pending final data'}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KPICard({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-white/10 border border-white/20 text-center">
      <p className="text-xs text-white/70 mb-1">{label}</p>
      <p className="text-2xl font-bold text-white">{value}%</p>
    </div>
  );
}

// ============================================================================
// FLYWHEEL TRACKER COMPONENT
// ============================================================================

const TAXONOMY_FLYWHEEL_STAGES = [
  { key: 'activity_identification', label: 'Activity Identification', description: 'Map economic activities to Taxonomy' },
  { key: 'substantial_contribution', label: 'Substantial Contribution', description: 'Assess technical screening criteria' },
  { key: 'dnsh_assessment', label: 'DNSH Assessment', description: 'Do No Significant Harm to other objectives' },
  { key: 'minimum_safeguards', label: 'Minimum Safeguards', description: 'Human rights & labor standards' },
  { key: 'evidence_collection', label: 'Evidence Collection', description: 'Gather supporting documentation' },
  { key: 'kpi_calculation', label: 'KPI Calculation', description: 'Calculate Turnover, CapEx, OpEx alignment' },
];

function TaxonomyFlywheelTracker({ progress }) {
  return (
    <Card className="bg-[#141414] border-[#2A2A2A]">
      <CardHeader>
        <CardTitle className="text-base text-white">Your Taxonomy Assessment Journey</CardTitle>
        <p className="text-sm text-gray-400">
          Defensibility flywheel for EU Taxonomy compliance
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {TAXONOMY_FLYWHEEL_STAGES.map((stage, idx) => {
            const stageProgress = progress[stage.key] || { status: 'not_started', percentage: 0, detail: '' };
            const isCompleted = stageProgress.status === 'completed';
            const isInProgress = stageProgress.status === 'in_progress';

            return (
              <div key={stage.key} className="flex items-start gap-4">
                {/* Stage Icon */}
                <div className={`
                  relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors
                  ${isCompleted ? 'border-emerald-500 bg-emerald-500/20' : ''}
                  ${isInProgress ? 'border-blue-500 bg-blue-500/20 ring-4 ring-blue-500/20' : ''}
                  ${!isCompleted && !isInProgress ? 'border-gray-600 bg-[#1A1A1A]' : ''}
                `}>
                  {isCompleted ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  ) : isInProgress ? (
                    <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-500" />
                  )}
                </div>

                {/* Stage Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <h4 className={`text-sm font-medium ${
                        isInProgress ? 'text-blue-300' :
                        isCompleted ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        {idx + 1}. {stage.label}
                      </h4>
                      <p className="text-xs text-gray-500">{stage.description}</p>
                    </div>
                    
                    {stageProgress.detail && (
                      <span className="text-xs font-medium text-gray-400 shrink-0 ml-2">
                        {stageProgress.detail}
                      </span>
                    )}
                  </div>

                  {/* Progress Bar */}
                  {stageProgress.status !== 'not_started' && (
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={stageProgress.percentage} 
                        className="h-1.5 flex-1 bg-[#2A2A2A]"
                      />
                      <span className="text-xs text-gray-400 shrink-0">
                        {stageProgress.percentage}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// ACTIVITY CARD COMPONENT
// ============================================================================

function ActivityCard({ activity, onViewDetails }) {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'aligned':
        return { icon: CheckCircle2, color: 'text-emerald-400', bgColor: 'bg-emerald-500/20', borderColor: 'border-emerald-500/30', label: 'Taxonomy-Aligned' };
      case 'in_progress':
        return { icon: Clock, color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', borderColor: 'border-yellow-500/30', label: 'Partially Assessed' };
      case 'not_aligned':
        return { icon: XCircle, color: 'text-red-400', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/30', label: 'Not Aligned' };
      default:
        return { icon: Clock, color: 'text-gray-400', bgColor: 'bg-gray-500/20', borderColor: 'border-gray-500/30', label: 'Not Started' };
    }
  };

  const statusConfig = getStatusConfig(activity.status);
  const StatusIcon = statusConfig.icon;

  return (
    <Card className={`bg-[#141414] border-[#2A2A2A] hover:border-[#3A3A3A] transition-colors ${statusConfig.borderColor}`}
          data-testid={`activity-card-${activity.activity_code}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="font-mono text-xs text-gray-300 border-gray-600">
                {activity.activity_code}
              </Badge>
              <Badge variant="outline" className="text-xs text-emerald-300 border-emerald-500/30">
                {activity.objective_label || activity.objective}
              </Badge>
            </div>
            <h3 className="text-lg font-semibold text-white">
              {activity.activity_name}
            </h3>
          </div>

          <Badge className={`shrink-0 ${statusConfig.bgColor} ${statusConfig.color} border-0`}>
            <StatusIcon className="h-3.5 w-3.5 mr-1" />
            {statusConfig.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Assessment Checklist */}
        <div className="space-y-2">
          <AssessmentCheckItem
            label="Substantial Contribution"
            status={activity.substantial_contribution?.status}
            detail={activity.substantial_contribution?.detail}
          />
          <AssessmentCheckItem
            label="DNSH (5 objectives)"
            status={activity.dnsh?.status}
            detail={activity.dnsh?.detail}
          />
          <AssessmentCheckItem
            label="Minimum Safeguards"
            status={activity.minimum_safeguards?.status}
            detail={activity.minimum_safeguards?.detail}
          />
        </div>

        {/* Financial Data */}
        {activity.financial_data && (
          <>
            <div className="h-px bg-[#2A2A2A]" />
            <div>
              <p className="text-xs font-semibold text-gray-400 mb-2">Financial Data:</p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-2 rounded bg-[#1A1A1A]">
                  <p className="text-gray-500 mb-0.5">Turnover</p>
                  <p className="font-semibold text-white">
                    €{(activity.financial_data.turnover / 1000000).toFixed(1)}M
                  </p>
                  <p className="text-gray-500">
                    ({activity.financial_data.turnover_percentage}% of total)
                  </p>
                </div>
                <div className="p-2 rounded bg-[#1A1A1A]">
                  <p className="text-gray-500 mb-0.5">CapEx</p>
                  <p className="font-semibold text-white">
                    €{(activity.financial_data.capex / 1000).toFixed(0)}K
                  </p>
                  <p className="text-gray-500">
                    ({activity.financial_data.capex_percentage}% of total)
                  </p>
                </div>
                <div className="p-2 rounded bg-[#1A1A1A]">
                  <p className="text-gray-500 mb-0.5">OpEx</p>
                  <p className="font-semibold text-white">
                    €{(activity.financial_data.opex / 1000).toFixed(0)}K
                  </p>
                  <p className="text-gray-500">
                    ({activity.financial_data.opex_percentage}% of total)
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Evidence & Owner */}
        <div className="h-px bg-[#2A2A2A]" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1 text-gray-400">
              <FileText className="h-4 w-4" />
              {activity.evidence_count || 0} evidence items
            </span>
          </div>
          
          {activity.owner && (
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <span className="text-xs text-emerald-400">{activity.owner.name?.charAt(0)}</span>
              </div>
              <span className="text-xs text-gray-400">{activity.owner.name}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 border-[#2A2A2A] text-gray-300 hover:bg-[#1A1A1A]"
            onClick={onViewDetails}
          >
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            View Details
          </Button>
          <Button variant="outline" size="sm" className="border-[#2A2A2A] text-gray-300 hover:bg-[#1A1A1A]">
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            Evidence
          </Button>
          <Button variant="outline" size="sm" className="border-[#2A2A2A] text-gray-300 hover:bg-[#1A1A1A]">
            <Shield className="h-3.5 w-3.5 mr-1.5" />
            Audit Trail
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AssessmentCheckItem({ label, status, detail }) {
  const config = {
    met: { icon: CheckCircle2, color: 'text-emerald-400', label: 'Met' },
    passed: { icon: CheckCircle2, color: 'text-emerald-400', label: 'Passed' },
    compliant: { icon: CheckCircle2, color: 'text-emerald-400', label: 'Compliant' },
    in_progress: { icon: Clock, color: 'text-yellow-400', label: 'In Progress' },
    failed: { icon: XCircle, color: 'text-red-400', label: 'Failed' },
    not_started: { icon: Circle, color: 'text-gray-500', label: 'Not Started' },
  };

  const itemConfig = config[status] || config.not_started;
  const Icon = itemConfig.icon;

  return (
    <div className="flex items-center justify-between p-2 rounded-lg bg-[#1A1A1A]">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${itemConfig.color}`} />
        <span className="text-sm text-gray-300">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {detail && <span className="text-xs text-gray-500">{detail}</span>}
        <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
          {itemConfig.label}
        </Badge>
      </div>
    </div>
  );
}

// ============================================================================
// OBJECTIVES OVERVIEW COMPONENT
// ============================================================================

const OBJECTIVE_ICONS = {
  climate_mitigation: Wind,
  climate_adaptation: Shield,
  water: Droplets,
  circular_economy: Recycle,
  pollution: Leaf,
  biodiversity: Trees
};

const OBJECTIVE_COLORS = {
  climate_mitigation: 'emerald',
  climate_adaptation: 'blue',
  water: 'cyan',
  circular_economy: 'amber',
  pollution: 'green',
  biodiversity: 'lime'
};

function ObjectivesOverview({ objectives }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {objectives.map(obj => {
        const Icon = OBJECTIVE_ICONS[obj.code] || Leaf;
        const color = OBJECTIVE_COLORS[obj.code] || 'gray';
        
        return (
          <Card key={obj.code} className="bg-[#141414] border-[#2A2A2A]" data-testid={`objective-${obj.code}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg bg-${color}-500/20`}>
                  <Icon className={`h-6 w-6 text-${color}-400`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-white mb-1">{obj.name}</h3>
                  <p className="text-xs text-gray-400 mb-3">{obj.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Activities</span>
                      <span className="text-white">{obj.aligned_count}/{obj.activities_count} aligned</span>
                    </div>
                    <Progress value={obj.alignment_percentage} className="h-1.5 bg-[#2A2A2A]" />
                    <div className="text-right">
                      <span className="text-xs font-medium text-emerald-400">{obj.alignment_percentage}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================================
// KPIs DASHBOARD COMPONENT
// ============================================================================

function KPIsDashboard({ kpis, activities }) {
  if (!kpis) return null;

  return (
    <div className="space-y-6">
      {/* Main KPI Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <KPIDetailCard 
          title="Turnover" 
          data={kpis.turnover}
          icon={TrendingUp}
          color="emerald"
        />
        <KPIDetailCard 
          title="CapEx" 
          data={kpis.capex}
          icon={BarChart3}
          color="blue"
        />
        <KPIDetailCard 
          title="OpEx" 
          data={kpis.opex}
          icon={Target}
          color="purple"
        />
      </div>

      {/* Breakdown by Objective */}
      <Card className="bg-[#141414] border-[#2A2A2A]">
        <CardHeader>
          <CardTitle className="text-base text-white">KPIs by Environmental Objective</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(kpis.by_objective || {}).map(([key, data]) => (
              <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-[#1A1A1A]">
                <span className="text-sm text-gray-300 capitalize">{key.replace(/_/g, ' ')}</span>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-gray-400">Activities: {data.aligned_activities}/{data.total_activities}</span>
                  <span className="text-emerald-400">€{(data.turnover / 1000000).toFixed(1)}M turnover</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function KPIDetailCard({ title, data, icon: Icon, color }) {
  return (
    <Card className="bg-[#141414] border-[#2A2A2A]">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-lg bg-${color}-500/20`}>
            <Icon className={`h-5 w-5 text-${color}-400`} />
          </div>
          <h3 className="text-sm font-semibold text-white">{title}</h3>
        </div>
        
        <div className="space-y-3">
          <div>
            <p className="text-3xl font-bold text-white">{data.aligned_percentage}%</p>
            <p className="text-xs text-gray-500">Taxonomy-aligned</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-2 rounded bg-[#1A1A1A]">
              <p className="text-gray-500">Aligned</p>
              <p className="text-white font-medium">€{(data.aligned / 1000000).toFixed(1)}M</p>
            </div>
            <div className="p-2 rounded bg-[#1A1A1A]">
              <p className="text-gray-500">Total</p>
              <p className="text-white font-medium">€{(data.total / 1000000).toFixed(1)}M</p>
            </div>
          </div>
          
          <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
            <p className="text-xs text-emerald-400 text-center">
              {data.eligible_percentage}% Taxonomy-eligible
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// EVIDENCE HUB COMPONENT
// ============================================================================

function TaxonomyEvidenceHub({ activities }) {
  const totalEvidence = activities.reduce((sum, a) => sum + (a.evidence_count || 0), 0);
  
  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card className="bg-[#141414] border-[#2A2A2A]">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">Evidence Collection</h3>
              <p className="text-sm text-gray-400">Supporting documentation for Taxonomy assessments</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-emerald-400">{totalEvidence}</p>
              <p className="text-xs text-gray-500">Total evidence items</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Evidence by Activity */}
      <Card className="bg-[#141414] border-[#2A2A2A]">
        <CardHeader>
          <CardTitle className="text-base text-white">Evidence by Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activities.map(activity => (
              <div key={activity.id} className="flex items-center justify-between p-3 rounded-lg bg-[#1A1A1A]">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="font-mono text-xs text-gray-300 border-gray-600">
                    {activity.activity_code}
                  </Badge>
                  <span className="text-sm text-gray-300 truncate max-w-md">
                    {activity.activity_name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                    {activity.evidence_count || 0} items
                  </Badge>
                  <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cross-Framework Intelligence */}
      <Card className="bg-[#141414] border-[#2A2A2A]">
        <CardHeader>
          <CardTitle className="text-base text-white">Cross-Framework Intelligence</CardTitle>
          <p className="text-sm text-gray-400">Evidence that also satisfies other frameworks</p>
        </CardHeader>
        <CardContent>
          <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <p className="text-sm text-emerald-400 mb-2">Upload once, use across frameworks</p>
            <p className="text-xs text-gray-400">
              Evidence uploaded for EU Taxonomy activities can automatically map to CSRD ESRS E1-5 
              (Climate Change) and CDP Climate disclosures.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// ARTICLE 8 REPORT COMPONENT
// ============================================================================

function Article8Report({ kpis, activities }) {
  return (
    <div className="space-y-6">
      {/* Report Overview */}
      <Card className="bg-[#141414] border-[#2A2A2A]">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">Article 8 Disclosure Report</h3>
              <p className="text-sm text-gray-400">Pre-filled KPI tables for Taxonomy disclosure</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="border-[#2A2A2A] text-gray-300 hover:bg-[#1A1A1A]">
                <FileText className="h-4 w-4 mr-2" />
                Preview Report
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </div>

          {/* KPI Summary Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2A2A2A]">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">KPI</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Total (€)</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Aligned (€)</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Aligned %</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Eligible %</th>
                </tr>
              </thead>
              <tbody>
                {kpis && (
                  <>
                    <tr className="border-b border-[#1A1A1A]">
                      <td className="py-3 px-4 text-white">Turnover</td>
                      <td className="py-3 px-4 text-right text-gray-300">
                        {(kpis.turnover.total / 1000000).toFixed(1)}M
                      </td>
                      <td className="py-3 px-4 text-right text-gray-300">
                        {(kpis.turnover.aligned / 1000000).toFixed(1)}M
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400 font-medium">
                        {kpis.turnover.aligned_percentage}%
                      </td>
                      <td className="py-3 px-4 text-right text-blue-400">
                        {kpis.turnover.eligible_percentage}%
                      </td>
                    </tr>
                    <tr className="border-b border-[#1A1A1A]">
                      <td className="py-3 px-4 text-white">CapEx</td>
                      <td className="py-3 px-4 text-right text-gray-300">
                        {(kpis.capex.total / 1000000).toFixed(1)}M
                      </td>
                      <td className="py-3 px-4 text-right text-gray-300">
                        {(kpis.capex.aligned / 1000000).toFixed(1)}M
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400 font-medium">
                        {kpis.capex.aligned_percentage}%
                      </td>
                      <td className="py-3 px-4 text-right text-blue-400">
                        {kpis.capex.eligible_percentage}%
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 text-white">OpEx</td>
                      <td className="py-3 px-4 text-right text-gray-300">
                        {(kpis.opex.total / 1000000).toFixed(1)}M
                      </td>
                      <td className="py-3 px-4 text-right text-gray-300">
                        {(kpis.opex.aligned / 1000000).toFixed(1)}M
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400 font-medium">
                        {kpis.opex.aligned_percentage}%
                      </td>
                      <td className="py-3 px-4 text-right text-blue-400">
                        {kpis.opex.eligible_percentage}%
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Audit Pack */}
      <Card className="bg-[#141414] border-[#2A2A2A]">
        <CardHeader>
          <CardTitle className="text-base text-white">Generate Audit Pack</CardTitle>
          <p className="text-sm text-gray-400">Complete documentation package for external verification</p>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
              <CheckCircle2 className="h-8 w-8 text-emerald-400 mb-2" />
              <h4 className="text-sm font-semibold text-white mb-1">Activity Assessments</h4>
              <p className="text-xs text-gray-400">{activities.length} activities documented</p>
            </div>
            <div className="p-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
              <FileText className="h-8 w-8 text-blue-400 mb-2" />
              <h4 className="text-sm font-semibold text-white mb-1">Supporting Evidence</h4>
              <p className="text-xs text-gray-400">
                {activities.reduce((sum, a) => sum + (a.evidence_count || 0), 0)} documents attached
              </p>
            </div>
            <div className="p-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
              <Shield className="h-8 w-8 text-purple-400 mb-2" />
              <h4 className="text-sm font-semibold text-white mb-1">Audit Trail</h4>
              <p className="text-xs text-gray-400">Complete change history</p>
            </div>
          </div>
          <Button className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700">
            Generate Complete Audit Pack
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}


// ============================================================================
// AI ASSESSMENT ASSISTANT
// ============================================================================

function AIAssessmentAssistant() {
  const [naceCodes, setNaceCodes] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [sector, setSector] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAnalyze = async () => {
    if (!naceCodes.trim()) {
      setError('Please enter at least one NACE code');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const res = await apiFetch(`/v1/ai/taxonomy/suggest-activities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nace_codes: naceCodes.split(',').map(c => c.trim()).filter(c => c),
          company_description: companyDescription,
          sector: sector
        })
      });

      if (res && !res._error) {
        setSuggestions(res);
      } else {
        setError('Failed to get suggestions. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('AI suggestion failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Introduction */}
      <Card className="bg-purple-500/10 border-purple-500/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-purple-300">
            <Sparkles className="h-5 w-5" />
            AI Assessment Assistant
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-400 mb-4">
            Get AI-powered suggestions for which EU Taxonomy activities apply to your company 
            based on your NACE codes and business activities. The assistant will also help you 
            understand the technical screening criteria for each suggested activity.
          </p>
          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
            <p className="text-sm text-purple-300">
              💡 Enter your company's NACE codes to discover applicable Taxonomy activities
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Input Form */}
      <Card className="bg-[#141414] border-[#2A2A2A]">
        <CardHeader>
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Building2 className="h-4 w-4 text-blue-400" />
            Company Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-300">
              NACE Codes <span className="text-red-400">*</span>
            </Label>
            <Input
              placeholder="e.g., D35.11, F43.21, C27.20 (comma-separated)"
              value={naceCodes}
              onChange={(e) => setNaceCodes(e.target.value)}
              className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
            />
            <p className="text-xs text-gray-500">
              Enter your company's NACE codes, separated by commas
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-300">Company Description</Label>
            <Textarea
              placeholder="Describe your company's main activities..."
              value={companyDescription}
              onChange={(e) => setCompanyDescription(e.target.value)}
              rows={3}
              className="bg-[#1A1A1A] border-[#2A2A2A] text-white resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-gray-300">Sector</Label>
            <select
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              className="w-full px-3 py-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-md text-white"
            >
              <option value="">Select sector</option>
              <option value="Energy">Energy</option>
              <option value="Manufacturing">Manufacturing</option>
              <option value="Construction">Construction</option>
              <option value="Transportation">Transportation</option>
              <option value="Water & Waste">Water & Waste</option>
              <option value="ICT">ICT</option>
              <option value="Real Estate">Real Estate</option>
              <option value="Professional Services">Professional Services</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <Button
            onClick={handleAnalyze}
            disabled={isLoading || !naceCodes.trim()}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Get AI Suggestions
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* AI Suggestions Results */}
      {suggestions && (
        <div className="space-y-4">
          {/* Summary */}
          <Card className="bg-emerald-500/10 border-emerald-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {suggestions.total_suggested || suggestions.suggested_activities?.length || 0} Activities Found
                  </h3>
                  <p className="text-sm text-emerald-300">
                    {suggestions.coverage_assessment}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Suggested Activities */}
          {suggestions.suggested_activities && suggestions.suggested_activities.length > 0 && (
            <Card className="bg-[#141414] border-[#2A2A2A]">
              <CardHeader>
                <CardTitle className="text-base text-white">Suggested Taxonomy Activities</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {suggestions.suggested_activities.map((activity, idx) => (
                  <div key={idx} className="p-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="font-mono text-xs text-emerald-300 border-emerald-500/30">
                            {activity.activity_code}
                          </Badge>
                          <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-xs">
                            {activity.objective_label || activity.objective}
                          </Badge>
                          {activity.relevance_score && (
                            <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 text-xs">
                              {Math.round(activity.relevance_score * 100)}% match
                            </Badge>
                          )}
                        </div>
                        <h4 className="text-white font-medium mb-2">
                          {activity.activity_name}
                        </h4>
                        {activity.reason && (
                          <p className="text-sm text-gray-400 mb-3">
                            {activity.reason}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Screening Criteria Summary */}
                    {activity.screening_criteria_summary && (
                      <div className="p-3 rounded bg-[#0D0D0D] mb-3">
                        <p className="text-xs font-medium text-gray-400 mb-1">Screening Criteria:</p>
                        <p className="text-sm text-gray-300">{activity.screening_criteria_summary}</p>
                      </div>
                    )}

                    {/* Key Thresholds */}
                    {activity.key_thresholds && activity.key_thresholds.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-400 mb-2">Key Thresholds:</p>
                        <div className="space-y-1">
                          {activity.key_thresholds.map((threshold, tidx) => (
                            <div key={tidx} className="flex items-center gap-2">
                              <Target className="h-3 w-3 text-emerald-400" />
                              <span className="text-sm text-gray-300">{threshold}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* DNSH Considerations */}
                    {activity.dnsh_considerations && activity.dnsh_considerations.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-400 mb-2">DNSH Considerations:</p>
                        <div className="space-y-1">
                          {activity.dnsh_considerations.map((dnsh, didx) => (
                            <div key={didx} className="flex items-center gap-2">
                              <AlertTriangle className="h-3 w-3 text-yellow-400" />
                              <span className="text-sm text-gray-300">{dnsh}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add to Assessment
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Next Steps */}
          {suggestions.next_steps && suggestions.next_steps.length > 0 && (
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardHeader>
                <CardTitle className="text-base text-blue-300 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Recommended Next Steps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="space-y-2">
                  {suggestions.next_steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                        <span className="text-xs font-semibold text-blue-400">{idx + 1}</span>
                      </div>
                      <span className="text-sm text-gray-300">{step}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
